import type { ErrorEvent } from "@sentry/react";

/**
 * Browsers inject their own scripts into the page (Chrome's translate feature
 * for users whose UI language differs from the app's, reader modes, in-app
 * browser bridges). Errors thrown there surface via `window.onerror` with no
 * script URL — the frames carry the page URL or `undefined` as filename — and
 * nothing in the app can fix or even observe them. Everything the app ships,
 * including vendored code, lives in hashed `.js` chunks, so an exception whose
 * frames reference no script file at all did not originate from our code.
 */

const SCRIPT_FILE_PATTERN = /\.(m?jsx?|c?js|tsx?)(?:[?#]|$)/i;

interface FrameLike {
  filename?: string;
  abs_path?: string;
}

const isScriptFrame = (frame: FrameLike): boolean => {
  const filename = frame.filename ?? frame.abs_path ?? "";
  return SCRIPT_FILE_PATTERN.test(filename);
};

const collectFrames = (event: ErrorEvent): FrameLike[] =>
  (event.exception?.values ?? []).flatMap(
    (value) => value.stacktrace?.frames ?? [],
  );

/**
 * True when the event has a stack trace and not a single frame of it points
 * at a script file. Events without frames (string throws, some promise
 * rejections) are never treated as foreign — there is nothing to judge by.
 */
export const isForeignScriptError = (event: ErrorEvent): boolean => {
  const frames = collectFrames(event);
  if (frames.length === 0) {
    return false;
  }
  return frames.every((frame) => !isScriptFrame(frame));
};

/** The first exception's headline, for the breadcrumb left in place of it. */
export const describeErrorEvent = (event: ErrorEvent): string => {
  const first = event.exception?.values?.[0];
  if (!first) {
    return event.message ?? "";
  }
  return [first.type, first.value].filter(Boolean).join(": ");
};
